// /devices/:id — admin device detail + lifecycle action bar (FACTS PACK §3c/§4).
//
// There is no GET /v3/admin/devices/{id} in the deployed contract, so detail hydrates
// by locating the row in the devices browse list (getDeviceById pages the forward-only
// cursor). The list row also passes the device via router state for an instant first
// paint while the fresh fetch runs. Per-device subscription is rendered when present
// (only surfaces via Customer-360, so it may be absent here — shown conditionally).
//
// Sensitive actions (disable / enable / report-lost) live in <DeviceActionBar>, each
// behind a ConfirmDialog stating the consequence; all are server-side audited.
//
// "在稽核紀錄中檢視" jumps to /audit pre-filled with this device as the target axis
// (target_id=device:<id>) — AuditPage reads that from the query string.

import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { formatRelativeUs, formatTimestampUs } from '../../lib/format';
import { CopyableId } from '../../components/CopyableId';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { AuditTrailPanel } from '../audit/AuditTrailPanel';
import { DiagnosticLockPanel } from '../diagnostics/DiagnosticLockPanel';
import { DeviceActionBar } from './DeviceActionBar';
import { DeviceDevlogPanel } from './DeviceDevlogPanel';
import {
  deviceName,
  deviceRoleLabel,
  deviceStatusLabel,
  deviceStatusTone,
  onlineLabel,
  onlineTone,
  recordingLocationLabel,
  recordingModeLabel,
  subStatusLabel,
  subStatusTone,
} from './deviceModel';
import { getDeviceById, isNotFound } from './devicesApi';
import type { AdminDevice } from './devicesApi';
import '../_shared/shared.css';
import './devices.css';

function StatusPill({ status }: { status?: string }) {
  return <span className={`pill pill-${deviceStatusTone(status)}`}>{deviceStatusLabel(status)}</span>;
}

// 右欄內容分頁(純本地 state,不寫 URL、不動路由)。與 user 360 的 .adm-tabs 心智一致:
// 處置(主操作,預設)/ 診斷鎖定 / 除錯 Log(僅寫入權可見)/ 處置紀錄。
type DeviceTab = 'action' | 'lock' | 'devlog' | 'audit';

interface LoadState {
  loading: boolean;
  error: unknown;
  notFound: boolean;
  device: AdminDevice | null;
}

export default function DeviceDetailPage() {
	const { id } = useParams();
	const { role } = useAuth();
	const deviceId = id ?? '';
  const location = useLocation();
  const navigate = useNavigate();

  // instant paint from the list row (router state), then refetch for freshness
  const seed = (location.state as { device?: AdminDevice } | null)?.device;
  const seedDevice = seed && seed.device_id === deviceId ? seed : null;

  const [state, setState] = useState<LoadState>({ loading: !seedDevice, error: null, notFound: false, device: seedDevice });
  const [tab, setTab] = useState<DeviceTab>('action');

  const load = useCallback(async () => {
    if (!deviceId) return;
    setState((s) => ({ ...s, loading: true, error: null, notFound: false }));
    try {
      const device = await getDeviceById(deviceId);
      setState({ loading: false, error: null, notFound: device === null, device });
    } catch (e) {
      if (isNotFound(e)) {
        setState({ loading: false, error: null, notFound: true, device: null });
      } else {
        setState({ loading: false, error: e instanceof ApiError ? e : new Error('載入失敗'), notFound: false, device: null });
      }
    }
  }, [deviceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const device = state.device;
  const sub = device?.subscription;

  return (
    <>
      <div className="page-header">
        <div>
          <button className="adm-back" onClick={() => navigate('/devices')}>
            ← 返回裝置清單
          </button>
          <h1 className="page-title">{device ? deviceName(device) : '裝置詳情'}</h1>
          <p className="page-subtitle mono">{deviceId}</p>
        </div>
        {/* 頂部裝置整體畫像:狀態/連線/型號三 pill 並排,不必捲到「基本資訊」卡才看得到 */}
        {device && (
          <div className="detail-summary">
            <StatusPill status={device.status} />
            <span className={`pill pill-${onlineTone(device)}`}>{onlineLabel(device)}</span>
            {device.model && <span className="pill pill-neutral adm-device-summary-model">{device.model}</span>}
          </div>
        )}
      </div>

      {/* show seed immediately even while refetching; only block when we have nothing */}
      {state.loading && !device && <LoadingState label="載入裝置詳情…" />}

      {!state.loading && state.error != null && <ErrorState error={state.error} onRetry={() => void load()} />}

      {!state.loading && state.notFound && !device && (
        <EmptyState
          icon="🔍"
          title="找不到裝置"
          detail={
            <>
              在裝置清單中找不到 ID 為「<span className="adm-mono">{deviceId}</span>」的裝置，可能已被移除或不在目前可見範圍。
            </>
          }
          action={
            <Link to="/devices" className="btn-ghost btn-sm">
              返回清單
            </Link>
          }
        />
      )}

      {device && (
        <div className="adm-detail-grid cols-2">
          {/* left: profile + subscription — sticky,右欄捲動時裝置身份摘要保持可見 */}
          <div className="adm-sticky-col">
            <section className="card">
              <h2 className="adm-card-title">基本資訊</h2>
              <dl className="adm-kv">
                <dt>裝置 ID</dt>
                <dd><CopyableId value={device.device_id} display={device.device_id} /></dd>
                <dt>顯示名稱</dt>
                <dd>{device.display_name || '—'}</dd>
                <dt>型號</dt>
                <dd>{device.model || '—'}</dd>
                <dt>基礎生態系</dt>
                <dd title={device.base_ecosystem_id || undefined}>
                  {device.base_ecosystem_name || device.base_ecosystem_id || '—'}
                  {device.product_org_name && <span className="text-tertiary">（{device.product_org_name}）</span>}
                </dd>
                <dt>擁有者生態系</dt>
                <dd title={device.owner_ecosystem_id || device.owner_family_id || undefined}>
                  {device.owner_ecosystem_name || device.owner_family_name || '—（無擁有者）'}
                </dd>
                <dt>加入生態系</dt>
                <dd>
                  {(() => {
                    const names = (device.joined_ecosystems ?? []).map((e) => e.name || e.id);
                    if (device.platform_union && device.base_ecosystem_id !== 'eco-kinio-ai') names.push('kinio.ai 平台聯盟');
                    return names.length > 0 ? names.join('、') : '—';
                  })()}
                </dd>
                <dt>韌體</dt>
                <dd>{device.firmware_version ? <CopyableId value={device.firmware_version} display={device.firmware_version} /> : '—'}</dd>
                <dt>連線</dt>
                <dd>
                  <span className={`pill pill-${onlineTone(device)}`}>{onlineLabel(device)}</span>
                  {device.online?.stale_hint && (
                    <span className="text-tertiary" style={{ marginLeft: 'var(--spacing-4)' }}>
                      （狀態可能過期）
                    </span>
                  )}
                </dd>
                <dt>關係</dt>
                <dd>{deviceRoleLabel(device.role)}</dd>
                <dt>錄影模式</dt>
                <dd>{recordingModeLabel(device.recording?.mode)}</dd>
                <dt>錄影位置</dt>
                <dd>{recordingLocationLabel(device.recording?.location)}</dd>
                <dt>錄影品質</dt>
                <dd>{device.recording?.quality || '—'}</dd>
                <dt>註冊時間</dt>
                <dd className="adm-mono">{formatTimestampUs(device.registered_at)}</dd>
                <dt>更新時間</dt>
                <dd className="adm-mono" title={formatTimestampUs(device.updated_at)}>
                  {formatRelativeUs(device.updated_at)}
                </dd>
              </dl>
            </section>

            <section className="card">
              <h2 className="adm-card-title">訂閱</h2>
              {sub ? (
                <dl className="adm-kv">
                  <dt>方案</dt>
                  <dd>{sub.plan || '—'}</dd>
                  <dt>狀態</dt>
                  <dd>
                    <span className={`pill pill-${subStatusTone(sub.status)}`}>{subStatusLabel(sub.status)}</span>
                  </dd>
                  <dt>到期</dt>
                  <dd className="adm-mono">{formatTimestampUs(sub.expired_time, { second: undefined })}</dd>
                  <dt>自動續訂</dt>
                  <dd>{sub.auto_renew == null ? '—' : sub.auto_renew ? '是' : '否'}</dd>
                  <dt>保留天數</dt>
                  <dd>{sub.retention_days == null ? '—' : `${sub.retention_days} 天`}</dd>
                  <dt>畫質上限</dt>
                  <dd>{sub.quality_allowed || '—'}</dd>
                </dl>
              ) : (
                /* 無訂閱資訊:輕量 inline empty-state,與「還沒載入完」在視覺上有區別 */
                <div className="empty-state empty-state--inline">
                  <div className="empty-state-icon" aria-hidden="true">
                    —
                  </div>
                  <p className="empty-state-detail" style={{ margin: 0 }}>
                    瀏覽清單未含訂閱資訊；裝置訂閱以「每台」為單位，可從擁有者的 Customer-360 檢視完整訂閱。
                  </p>
                </div>
              )}
            </section>
          </div>

          {/* right: 內容 tab(純本地 state,不動路由)——與 user 360 右欄同款 .adm-tabs;
              預設「處置」= DeviceActionBar(本頁唯一高風險/不可逆操作,主操作)。 */}
          <div>
            <div className="adm-tabs" role="tablist" aria-label="裝置 360 內容分頁">
              <button role="tab" aria-selected={tab === 'action'} className={`adm-tab${tab === 'action' ? ' is-active' : ''}`} onClick={() => setTab('action')}>
                處置
              </button>
              <button role="tab" aria-selected={tab === 'lock'} className={`adm-tab${tab === 'lock' ? ' is-active' : ''}`} onClick={() => setTab('lock')}>
                診斷鎖定
              </button>
              {canAdminWrite(role) && (
                <button role="tab" aria-selected={tab === 'devlog'} className={`adm-tab${tab === 'devlog' ? ' is-active' : ''}`} onClick={() => setTab('devlog')}>
                  除錯 Log
                </button>
              )}
              <button role="tab" aria-selected={tab === 'audit'} className={`adm-tab${tab === 'audit' ? ' is-active' : ''}`} onClick={() => setTab('audit')}>
                處置紀錄
              </button>
            </div>

            <div className="d-tab-body">
              {tab === 'action' && <DeviceActionBar device={device} onChanged={() => void load()} />}

              {tab === 'lock' && <DiagnosticLockPanel targetType="device" targetId={deviceId} />}

              {tab === 'devlog' && canAdminWrite(role) && <DeviceDevlogPanel deviceId={deviceId} />}

              {/* C6:內嵌處置紀錄(取代原「稽核」跳頁卡;case_id chip 深連 /audit) */}
              {tab === 'audit' && <AuditTrailPanel targetId={`device:${deviceId}`} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
